import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from '@aws-amplify/auth';
import { Button, Alert, Form, Badge, Spinner } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth';
import './LoginPage.css';

export function TotpSetupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [mfaStatus, setMfaStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [statusError, setStatusError] = useState(null);

  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpError, setTotpError] = useState(null);
  const [totpSuccess, setTotpSuccess] = useState(false);

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const loadMfaStatus = async () => {
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const prefs = await fetchMFAPreference();
      setMfaStatus(prefs);
    } catch (err) {
      setStatusError(err.message || 'Failed to load MFA status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const totpOn = mfaStatus?.enabled?.includes('TOTP') ?? false;

  const handleStartTotpSetup = async () => {
    setTotpError(null);
    setTotpLoading(true);
    try {
      const output = await setUpTOTP();
      const issuer = 'TegratAuto';
      const accountName = user?.email || 'user';
      const qrUrl = `otpauth://totp/${issuer}:${accountName}?secret=${output.sharedSecret}&issuer=${issuer}`;
      setTotpSetup({ qrUrl, secret: output.sharedSecret });
    } catch (err) {
      setTotpError(err.message || 'Failed to start TOTP setup');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleVerifyTotp = async (e) => {
    e.preventDefault();
    setTotpError(null);
    setTotpLoading(true);
    try {
      await verifyTOTPSetup({ code: totpCode });
      await updateMFAPreference({ totp: 'ENABLED' });
      setTotpSuccess(true);
      setTotpSetup(null);
      setTotpCode('');
      await loadMfaStatus();
    } catch (err) {
      setTotpError(err.message || 'Invalid TOTP code. Please try again.');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleDisableTotp = async () => {
    setTotpError(null);
    setTotpLoading(true);
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      await loadMfaStatus();
    } catch (err) {
      setTotpError(err.message || 'Failed to disable authenticator app');
    } finally {
      setTotpLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="tg-auth-card tg-auth-card-wide">
        <div className="tg-auth-header">
          <div className="tg-auth-header-text">
            <h2>Authenticator App (TOTP)</h2>
            <p>Set up two-factor authentication using an authenticator app</p>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
            &larr; Back
          </Button>
        </div>

        {loadingStatus ? (
          <div className="text-center py-4" style={{ color: 'var(--ink-3)' }}>
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : statusError ? (
          <Alert variant="danger">{statusError}</Alert>
        ) : (
          <>
            <div className="tg-auth-status-row">
              <span style={{ color: 'var(--ink-3)', fontSize: 13 }}>Status</span>
              <Badge bg={totpOn ? 'success' : 'secondary'}>
                {totpOn ? 'Enabled' : 'Not configured'}
              </Badge>
              {totpOn && mfaStatus?.preferred === 'TOTP' && (
                <Badge bg="info" text="dark">Preferred</Badge>
              )}
            </div>

            {totpError && <Alert variant="danger">{totpError}</Alert>}
            {totpSuccess && <Alert variant="success">Authenticator app set up successfully!</Alert>}

            {!totpSetup ? (
              <div className="d-flex gap-2">
                <Button variant="outline-primary" onClick={handleStartTotpSetup} disabled={totpLoading}>
                  {totpLoading
                    ? <Spinner animation="border" size="sm" />
                    : (totpOn ? 'Reconfigure' : 'Set up Authenticator App')}
                </Button>
                {totpOn && (
                  <Button variant="outline-danger" onClick={handleDisableTotp} disabled={totpLoading}>
                    Disable
                  </Button>
                )}
              </div>
            ) : (
              <div>
                <p className="tg-auth-desc">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.),
                  then enter the 6-digit code to confirm.
                </p>
                <div className="tg-auth-qr">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(totpSetup.qrUrl)}`}
                    alt="TOTP QR Code"
                    width={180}
                    height={180}
                  />
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    Manual key: <code>{totpSetup.secret}</code>
                  </div>
                </div>
                <Form onSubmit={handleVerifyTotp} className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="6-digit code"
                    disabled={totpLoading}
                    required
                    autoFocus
                    style={{ maxWidth: 160 }}
                  />
                  <Button type="submit" variant="primary" disabled={totpLoading}>
                    {totpLoading ? <Spinner animation="border" size="sm" /> : 'Verify & Enable'}
                  </Button>
                  <Button variant="outline-secondary" onClick={() => setTotpSetup(null)} disabled={totpLoading}>
                    Cancel
                  </Button>
                </Form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TotpSetupPage;
